import type { ReactNode } from "react";
import { RouterProvider } from "react-router";
import { ThemeProvider, useTheme } from "next-themes";
import {
  ChainType,
  WalletKitProvider,
  type Locals,
  type Theme,
} from "@web3jskit/walletkit";
import { router } from "./routes";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { useIsAuthenticated } from "../stores/modules/auth";
import { useDepositStore } from "../stores/modules/deposit";
import { useDepositBlockchainsStore } from "../stores/modules/depositBlockchains";

/**
 * Bridges the project theme (next-themes) into WalletKit's controlled `theme`
 * prop so the wallet UI follows the app's light/dark mode. Must live inside the
 * next-themes ThemeProvider to read its context.
 */
function WalletKitThemeBridge({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const { resolvedTheme, theme } = useTheme();
  const walletKitTheme: Theme =
    (resolvedTheme ?? theme) === "light" ? "light" : "dark";

  return (
    <WalletKitProvider
      defaultChainType={ChainType.EVM}
      language={i18n.language as Locals}
      theme={walletKitTheme}
    >
      {children}
    </WalletKitProvider>
  );
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { refreshPlans } = useDepositStore();

  // 应用启动即拉取已启用链列表（登录页展示 / 登录后切链）
  useEffect(() => {
    void useDepositBlockchainsStore.getState().refreshBlockchains();
  }, []);

  // 登录后自动获取充值计划
  useEffect(() => {
    // 只有在已登录且不在登录页时才获取
    if (isAuthenticated && window.location.pathname !== '/login') {
      // 延迟一下再获取，确保 token 已经完全同步
      const timer = setTimeout(() => {
        refreshPlans();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, refreshPlans]);
  
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <WalletKitThemeBridge>
        <RouterProvider router={router} />
      </WalletKitThemeBridge>
    </ThemeProvider>
  );
}
